import { useEffect, useState } from "react";

// ─── useBoardDensity ───────────────────────────────────────────────────
//
// Persisted user preference for how dense The Grid's day swimlane renders —
// how wide the job cards are, how tall the lanes are, and how much card
// chrome shows. This is ORTHOGONAL to `useUiDensity` (which only sets the
// page container's max-width): board density is about the cards themselves.
//
// - "comfortable" (default): readable cards, taller lanes, address shown.
//   The small-screen-friendly default — everything legible out of the box.
// - "compact": denser cards, shorter lanes, address hidden. The widescreen
//   "see the whole day at once" overview (an opt-in benefit, never required).
//
// Backed by localStorage so the choice survives reloads and is kept in sync
// across independent consumers (the command bar's toggle and the swimlane,
// which never share a React tree) via a custom in-tab event plus the native
// cross-tab `storage` event — mirrors `use-ui-density.ts`.

export type BoardDensity = "comfortable" | "compact";

export const BOARD_DENSITY_STORAGE_KEY = "dispatch.board-density";

const DEFAULT_DENSITY: BoardDensity = "comfortable";

// Fired in-tab whenever the density changes so other hook instances in the
// same document re-read the value (the native `storage` event only fires in
// *other* tabs, not the one that wrote it).
const DENSITY_EVENT = "board-density-change";

function isBoardDensity(value: unknown): value is BoardDensity {
    return value === "comfortable" || value === "compact";
}

function readStoredDensity(): BoardDensity {
    if (typeof window === "undefined") return DEFAULT_DENSITY;
    const stored = window.localStorage.getItem(BOARD_DENSITY_STORAGE_KEY);
    return isBoardDensity(stored) ? stored : DEFAULT_DENSITY;
}

export interface UseBoardDensityResult {
    density: BoardDensity;
    setDensity: (next: BoardDensity) => void;
}

export function useBoardDensity(): UseBoardDensityResult {
    const [density, setDensityState] = useState<BoardDensity>(readStoredDensity);

    // Re-sync when another consumer (same tab) or another tab changes it.
    useEffect(() => {
        const sync = () => setDensityState(readStoredDensity());
        window.addEventListener(DENSITY_EVENT, sync);
        window.addEventListener("storage", sync);
        return () => {
            window.removeEventListener(DENSITY_EVENT, sync);
            window.removeEventListener("storage", sync);
        };
    }, []);

    const setDensity = (next: BoardDensity) => {
        setDensityState(next);
        if (typeof window !== "undefined") {
            window.localStorage.setItem(BOARD_DENSITY_STORAGE_KEY, next);
            window.dispatchEvent(new CustomEvent(DENSITY_EVENT));
        }
    };

    return { density, setDensity };
}
